import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';

export interface AutosaveOptions<T> {
  value: T;
  save: (value: T) => Promise<void>;
  /** Debounce before saving after the last change. */
  delay?: number;
  /** Retry delay after a failed save (network, 5xx). */
  retryDelay?: number;
  /** Returns true for errors that must stop autosave until the user decides (e.g. revision conflict). */
  isConflict?: (err: unknown) => boolean;
}

/**
 * Debounced, serialized autosave (FR-04). Changes made while a save is in
 * flight are saved right after it, so the latest edit always wins and saves
 * never overlap (the server's revision check would reject overlapping ones).
 */
export function useAutosave<T>({ value, save, delay = 800, retryDelay = 5000, isConflict }: AutosaveOptions<T>) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const latest = useRef(value);
  const saved = useRef(value);
  const inFlight = useRef(false);
  const blocked = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const conflictRef = useRef(isConflict);
  conflictRef.current = isConflict;

  const run = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (inFlight.current || blocked.current || latest.current === saved.current) return;
    inFlight.current = true;
    const snapshot = latest.current;
    setStatus('saving');
    try {
      await saveRef.current(snapshot);
      saved.current = snapshot;
      setError(null);
      setSavedAt(new Date());
      inFlight.current = false;
      if (latest.current !== snapshot) {
        setStatus('pending');
        void run();
      } else {
        setStatus('saved');
      }
    } catch (err) {
      inFlight.current = false;
      setError(err);
      if (conflictRef.current?.(err)) {
        blocked.current = true;
        setStatus('conflict');
        return;
      }
      setStatus('error');
      timer.current = setTimeout(() => void run(), retryDelay);
    }
  }, [retryDelay]);

  useEffect(() => {
    latest.current = value;
    if (value === saved.current || blocked.current) return;
    setStatus((s) => (s === 'saving' ? s : 'pending'));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void run(), delay);
  }, [value, delay, run]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Warn before leaving with unsaved edits.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (latest.current !== saved.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  /** Accept `value` as the saved baseline (after loading or resolving a conflict). */
  const reset = useCallback((v: T) => {
    if (timer.current) clearTimeout(timer.current);
    blocked.current = false;
    latest.current = v;
    saved.current = v;
    setStatus('idle');
    setError(null);
  }, []);

  return { status, error, savedAt, flush: run, reset, dirty: status === 'pending' || status === 'saving' || status === 'error' };
}
